import React, { useState, useRef, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './SimpleDateRangePicker.css';
import moment from 'moment';

const SimpleDateRangePicker = ({ startDate, endDate, onChange, placeholder = "Select Date Range", className = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localStartDate, setLocalStartDate] = useState(startDate);
  const [localEndDate, setLocalEndDate] = useState(endDate);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setLocalStartDate(startDate);
    setLocalEndDate(endDate);
  }, [startDate, endDate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleQuickSelect = (range) => {
    let start, end;
    const now = moment();

    switch (range) {
      case 'today':
        start = now.startOf('day').toDate();
        end = now.endOf('day').toDate();
        break;
      case 'yesterday':
        start = now.subtract(1, 'days').startOf('day').toDate();
        end = moment().subtract(1, 'days').endOf('day').toDate();
        break;
      case 'thisWeek':
        start = moment().startOf('week').toDate();
        end = moment().endOf('week').toDate();
        break;
      case 'lastWeek':
        start = moment().subtract(1, 'weeks').startOf('week').toDate();
        end = moment().subtract(1, 'weeks').endOf('week').toDate();
        break;
      case 'thisMonth':
        start = moment().startOf('month').toDate();
        end = moment().endOf('month').toDate();
        break;
      case 'lastMonth':
        start = moment().subtract(1, 'months').startOf('month').toDate();
        end = moment().subtract(1, 'months').endOf('month').toDate();
        break;
      case 'last7Days':
        start = moment().subtract(6, 'days').startOf('day').toDate();
        end = moment().endOf('day').toDate();
        break;
      case 'last30Days':
        start = moment().subtract(29, 'days').startOf('day').toDate();
        end = moment().endOf('day').toDate();
        break;
      case 'thisYear':
        start = moment().startOf('year').toDate();
        end = moment().endOf('year').toDate();
        break;
      default:
        return;
    }

    setLocalStartDate(start);
    setLocalEndDate(end);
    onChange([start, end]);
    setIsOpen(false);
  };

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setLocalStartDate(start);
    setLocalEndDate(end);
    
    if (start && end) {
      onChange([start, end]);
      setIsOpen(false);
    }
  };

  const getDisplayText = () => {
    if (localStartDate && localEndDate) {
      return `${moment(localStartDate).format('MMM DD, YYYY')} - ${moment(localEndDate).format('MMM DD, YYYY')}`;
    }
    return placeholder;
  };

  const getActiveRange = () => {
    if (!localStartDate || !localEndDate) return null;

    const start = moment(localStartDate).startOf('day');
    const end = moment(localEndDate).endOf('day');
    const now = moment();

    // Check Today
    if (start.isSame(now.clone().startOf('day'), 'day') && 
        end.isSame(now.clone().endOf('day'), 'day')) {
      return 'today';
    }

    // Check Yesterday
    if (start.isSame(now.clone().subtract(1, 'days').startOf('day'), 'day') && 
        end.isSame(now.clone().subtract(1, 'days').endOf('day'), 'day')) {
      return 'yesterday';
    }

    // Check This Week
    if (start.isSame(moment().startOf('week'), 'day') && 
        end.isSame(moment().endOf('week'), 'day')) {
      return 'thisWeek';
    }

    // Check Last Week
    if (start.isSame(moment().subtract(1, 'weeks').startOf('week'), 'day') && 
        end.isSame(moment().subtract(1, 'weeks').endOf('week'), 'day')) {
      return 'lastWeek';
    }

    // Check This Month
    if (start.isSame(moment().startOf('month'), 'day') && 
        end.isSame(moment().endOf('month'), 'day')) {
      return 'thisMonth';
    }

    // Check Last Month
    if (start.isSame(moment().subtract(1, 'months').startOf('month'), 'day') && 
        end.isSame(moment().subtract(1, 'months').endOf('month'), 'day')) {
      return 'lastMonth';
    }

    // Check Last 7 Days
    if (start.isSame(moment().subtract(6, 'days').startOf('day'), 'day') && 
        end.isSame(moment().endOf('day'), 'day')) {
      return 'last7Days';
    }

    // Check Last 30 Days
    if (start.isSame(moment().subtract(29, 'days').startOf('day'), 'day') && 
        end.isSame(moment().endOf('day'), 'day')) {
      return 'last30Days';
    }

    // Check This Year
    if (start.isSame(moment().startOf('year'), 'day') && 
        end.isSame(moment().endOf('year'), 'day')) {
      return 'thisYear';
    }

    return null;
  };

  const activeRange = getActiveRange();

  return (
    <div className={`simple-date-range-picker ${className}`} ref={dropdownRef}>
      <div 
        className="date-range-input"
        onClick={() => setIsOpen(!isOpen)}
      >
        <i className="fa fa-calendar me-2"></i>
        <span>{getDisplayText()}</span>
        <i className={`fa fa-chevron-${isOpen ? 'up' : 'down'} ms-auto`}></i>
      </div>

      {isOpen && (
        <div className="date-range-dropdown">
          <div className="date-range-content">
            {/* Quick Select Section */}
            <div className="quick-select-section">
              <div className="quick-select-header">
                <i className="fa fa-clock me-2"></i>
                Quick Select
              </div>
              
              <div className="quick-select-grid">
                <button
                  className={`quick-select-btn ${activeRange === 'today' ? 'active' : ''}`}
                  onClick={() => handleQuickSelect('today')}
                >
                  Today
                </button>
                <button
                  className={`quick-select-btn ${activeRange === 'yesterday' ? 'active' : ''}`}
                  onClick={() => handleQuickSelect('yesterday')}
                >
                  Yesterday
                </button>
                <button
                  className={`quick-select-btn ${activeRange === 'thisWeek' ? 'active' : ''}`}
                  onClick={() => handleQuickSelect('thisWeek')}
                >
                  This Week
                </button>
                <button
                  className={`quick-select-btn ${activeRange === 'lastWeek' ? 'active' : ''}`}
                  onClick={() => handleQuickSelect('lastWeek')}
                >
                  Last Week
                </button>
                <button
                  className={`quick-select-btn ${activeRange === 'thisMonth' ? 'active' : ''}`}
                  onClick={() => handleQuickSelect('thisMonth')}
                >
                  This Month
                </button>
                <button
                  className={`quick-select-btn ${activeRange === 'lastMonth' ? 'active' : ''}`}
                  onClick={() => handleQuickSelect('lastMonth')}
                >
                  Last Month
                </button>
                <button
                  className={`quick-select-btn ${activeRange === 'last7Days' ? 'active' : ''}`}
                  onClick={() => handleQuickSelect('last7Days')}
                >
                  Last 7 Days
                </button>
                <button
                  className={`quick-select-btn ${activeRange === 'last30Days' ? 'active' : ''}`}
                  onClick={() => handleQuickSelect('last30Days')}
                >
                  Last 30 Days
                </button>
                <button
                  className={`quick-select-btn ${activeRange === 'thisYear' ? 'active' : ''}`}
                  onClick={() => handleQuickSelect('thisYear')}
                >
                  This Year
                </button>
              </div>
            </div>

            {/* Date Picker Section */}
            <div className="date-picker-section">
              <DatePicker
                selected={localStartDate}
                onChange={handleDateChange}
                startDate={localStartDate}
                endDate={localEndDate}
                selectsRange
                inline
                monthsShown={2}
                maxDate={new Date()}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimpleDateRangePicker;

